import type { DashboardDefinition } from "@k8s-dashboard/dashboard-schema";
import type { APIError } from "./api-error";
import type { SloSummary } from "./slo";
import type { RolloutCompare } from "./rollout-compare";
import type { MetricInvestigation } from "./metric-investigation";
import type {
  WarningEventFilters,
  WarningEventLimits,
  WarningEventPageMeta,
  WarningEventRow,
} from "./warning-events";

/**
 * @k8s-dashboard/contracts
 * --------------------------------------------------------------------------
 * Observability API/BFF와 Web UI가 공유하는 응답 계약입니다.
 * 이후 OpenAPI에서 생성하도록 바꿀 수 있으나, 지금은 손으로 유지합니다.
 *
 * 설계 규칙
 * - **화면 단위 집계 응답.** Cluster Overview는 위젯마다 호출하지 않고
 *   `GET /api/v1/clusters/{clusterId}/overview` 하나로 받습니다. (이슈 #14 완료 기준:
 *   초기 로딩에서 N+1 요청이 발생하지 않을 것)
 * - **부분 장애를 값으로 표현합니다.** 패널 단위로 `Section<T>`를 쓰고, 한 데이터소스가
 *   죽어도 나머지 섹션은 정상 값을 유지합니다. 화면은 섹션별로 Degraded를 표시합니다.
 * - **권한 없음은 에러가 아니라 상태입니다.** `status: "forbidden"`으로 내려오고,
 *   화면은 "데이터 없음"과 다르게 렌더링합니다.
 * - Scope(cluster/namespace)는 요청 파라미터를 신뢰하지 않고 서버가 토큰에서 강제 삽입합니다.
 *   UI가 보내는 값은 힌트일 뿐입니다. (README §10)
 */

/* ── 공통 ────────────────────────────────────────────────────────────────── */

/** 시간 범위 프리셋. Custom의 최대 폭은 30일이며 서버가 강제합니다. */
export type RangeKey = "1h" | "1d" | "7d" | "30d" | "custom";

/** 범위별 강제 Step(초). 사용자가 고르지 않습니다. (README §11) */
export const STEP_SECONDS: Record<Exclude<RangeKey, "custom">, number> = {
  "1h": 60,
  "1d": 300,
  "7d": 900,
  "30d": 3600,
};

export const RANGE_LABEL: Record<RangeKey, string> = {
  "1h": "최근 1시간",
  "1d": "최근 1일",
  "7d": "최근 7일",
  "30d": "최근 30일",
  custom: "Custom",
};

export const STEP_LABEL: Record<Exclude<RangeKey, "custom">, string> = {
  "1h": "1분",
  "1d": "5분",
  "7d": "15분",
  "30d": "1시간",
};

/** 정규화된 심각도. 색과 아이콘은 design-system의 status 토큰에 대응합니다. */
export type Severity = "healthy" | "progressing" | "warning" | "degraded" | "critical" | "unknown";

/**
 * 패널 단위 봉투(envelope).
 * - ok        : 값이 있음
 * - empty     : 권한도 있고 조회도 성공했지만 결과가 0건
 * - forbidden : Scope 밖이라 조회 자체가 거절됨
 * - degraded  : 데이터소스 일부 장애. `data`가 있으면 stale 값이라도 보여줍니다.
 */
export type SectionStatus = "ok" | "empty" | "forbidden" | "degraded";

export interface Section<T> {
  status: SectionStatus;
  /** degraded일 때도 마지막으로 성공한 값이 있으면 함께 내려옵니다. */
  data?: T;
  /**
   * 어떤 데이터소스가 문제인지. 화면 문구에 그대로 노출합니다.
   * `grafana`는 해소 이력 원천입니다(ADR 0049) — Alertmanager 장애와 다른 사실이라
   * 이름을 나눕니다. 미구성처럼 장애가 아닌 사유에는 출처를 붙이지 않습니다.
   */
  source?: "greptimedb" | "quickwit" | "kubernetes" | "alertmanager" | "grafana";
  /** 사람이 읽는 사유. 스택트레이스나 내부 식별자를 담지 않습니다. */
  reason?: string;
  /** 이 섹션 값이 만들어진 시각(RFC3339). stale 판정에 씁니다. */
  observedAt?: string;
}

/* ── 엔티티 ──────────────────────────────────────────────────────────────── */

/** 식별 우선순위: Pod UID → Workload UID → ns+kind+name → Pod Name (README §5) */
export interface EntityRef {
  clusterId: string;
  namespace?: string;
  workloadKind?: "Deployment" | "StatefulSet" | "DaemonSet" | "ReplicaSet" | "CronJob";
  workloadName?: string;
  workloadUid?: string;
  podName?: string;
  podUid?: string;
  containerName?: string;
  /** OpenTelemetry service.* 대응. namespace/version은 serviceName이 있어야 의미가 있습니다. */
  serviceName?: string;
  serviceNamespace?: string;
  serviceVersion?: string;
}

/* ── Unified Telemetry Model (이슈 #4) ──────────────────────────────────── */
export * from "./telemetry";

/* ── Overview 섹션별 페이로드 ───────────────────────────────────────────── */

export interface NodeHealth {
  total: number;
  ready: number;
  notReady: number;
  /** MemoryPressure/DiskPressure/PIDPressure 중 하나라도 걸린 노드 수 */
  pressure: number;
  unschedulable: number;
}

export interface PodHealth {
  total: number;
  running: number;
  pending: number;
  failed: number;
  crashLoopBackOff: number;
  imagePullBackOff: number;
  /** 최근 범위 내 재시작 합계 */
  restarts: number;
}

export interface WorkloadHealth {
  total: number;
  available: number;
  /** desired와 ready replica가 다른 워크로드 수 */
  replicaMismatch: number;
  /** progressDeadline을 넘긴 롤아웃 */
  rolloutStalled: number;
}

/**
 * 차트가 그릴 수 있는 단위의 **닫힌 집합**. 노드 시계열도 같은 집합을 씁니다 —
 * 단위가 갈라지면 같은 값이 화면마다 다른 축으로 그려집니다.
 */
export type MetricUnit =
  | "percent"
  | "bytes"
  | "bytes_per_sec"
  | "count"
  | "cores"
  | "millicores"
  | "mebibytes";

export interface TrendPoint {
  /** epoch milliseconds */
  t: number;
  v: number;
}

export interface TrendSeries {
  key: string;
  label: string;
  /** Query Catalog의 unit 값과 1:1입니다. 값은 원시 단위이며 표시는 UI가 자동 환산합니다. (#31) */
  unit: MetricUnit;
  points: TrendPoint[];
}

export interface TrendPanel {
  id: "cpu" | "memory" | "network" | "io" | "restarts";
  title: string;
  stepSeconds: number;
  series: TrendSeries[];
}

export interface UnhealthyEntity {
  ref: EntityRef;
  /** 표시용 이름. Pod가 있으면 Pod 이름, 없으면 Workload 이름 */
  name: string;
  kind: "Pod" | "Workload" | "Node";
  namespace: string;
  severity: Severity;
  /** 정규화된 사유. CrashLoopBackOff, Replica 2/3 등 */
  reason: string;
  restarts: number;
  /** 이 상태가 유지된 시간(초) */
  forSeconds: number;
}

export interface ClusterEvent {
  id: string;
  type: "Normal" | "Warning";
  reason: string;
  message: string;
  involved: EntityRef;
  involvedName: string;
  namespace: string;
  count: number;
  lastSeen: string;
}

/**
 * Warning Events 화면 **한 요청**의 전부입니다(#208).
 *
 * `appliedScope`는 클러스터 권한 전체가 아니라 **이 요청이 실제로 본 범위**입니다 —
 * 전역 namespace 선택이 하나면 그 하나이고, 그것이 행·건수·링크를 가른 값입니다.
 */
export interface WarningEventsResponse {
  clusterId: string;
  generatedAt: string;
  appliedScope: { clusterId: string; namespaces: string[] | "all" };
  range: { key: RangeKey; from: string; to: string; stepSeconds: number };
  /** 서버가 정규화한 필터. 입력 원문 에코가 아닙니다. */
  filters: WarningEventFilters;
  events: Section<WarningEventRow[]>;
  page: WarningEventPageMeta;
  limits: WarningEventLimits;
}

export interface AlertSummary {
  /** Grafana Alerting/Alertmanager의 severity를 그대로 씁니다. */
  bySeverity: { critical: number; warning: number; info: number };
  /** 대표 알림 몇 건. 전체 목록은 Alert 화면에서 봅니다. */
  top: Array<{
    id: string;
    name: string;
    severity: "critical" | "warning" | "info";
    namespace: string;
    activeSince: string;
  }>;
}

export interface TopologyEdgeSummary {
  from: string;
  to: string;
  protocol: "HTTP" | "gRPC" | "TCP" | "UDP";
  requestsPerSecond: number;
  errorRate: number;
  severity: Severity;
}

export interface TopologySummary {
  pods: number;
  edges: number;
  /** 문제 있는 경로만 축약해서 보여줍니다. 전체는 Topology 화면. */
  problemEdges: TopologyEdgeSummary[];
}

/* ── 화면 단위 응답 ─────────────────────────────────────────────────────── */

export interface ClusterOverviewResponse {
  clusterId: string;
  clusterName: string;
  /** 서버가 강제 적용한 Scope. UI가 보낸 값과 다를 수 있습니다. */
  appliedScope: { clusterId: string; namespaces: string[] | "all" };
  range: { key: RangeKey; from: string; to: string; stepSeconds: number };
  generatedAt: string;

  nodes: Section<NodeHealth>;
  pods: Section<PodHealth>;
  workloads: Section<WorkloadHealth>;
  trends: Section<TrendPanel[]>;
  unhealthy: Section<UnhealthyEntity[]>;
  events: Section<ClusterEvent[]>;
  alerts: Section<AlertSummary>;
  topology: Section<TopologySummary>;
}

/** Scope Selector가 쓰는 목록. 사용자가 볼 수 있는 범위만 내려옵니다. */
/* ── Workload/Secret 관리 (ADR 0014, #32) ─────────────────────────────── */

export interface ManagedWorkload {
  namespace: string;
  name: string;
  kind: "Deployment" | "Secret";
  ready: number;
  desired: number;
  secretType?: string;
  updatedAt: string;
}

export interface ManagedWorkloadListResponse {
  clusterId: string;
  generatedAt: string;
  items: ManagedWorkload[];
}

export interface ManagedPod {
  name: string;
  uid: string;
  namespace: string;
  phase: string;
  ready: boolean;
  restarts: number;
  severity: Severity;
}

export interface ManagedDeploymentDetail {
  clusterId: string;
  namespace: string;
  name: string;
  generatedAt: string;
  ready: number;
  desired: number;
  /** managedFields·status를 제거한 관리자용 JSON 매니페스트 문자열. */
  manifest: string;
  pods: ManagedPod[];
}

export interface ManagedSecretDetail {
  clusterId: string;
  namespace: string;
  name: string;
  generatedAt: string;
  secretType: string;
  /** key → 평문 값(서버가 base64 디코딩). ADR 0014 — admin 전용 노출. */
  data: Record<string, string>;
  pods: ManagedPod[];
}

export interface ManagedActionResult {
  ok: boolean;
  message: string;
  affected?: string[];
}

export type ManualWorkloadKind = "Deployment" | "StatefulSet" | "DaemonSet";
export type ManualWorkloadAction = "Scale" | "Restart";

export interface ManualWorkloadTarget {
  kind: ManualWorkloadKind;
  namespace: string;
  name: string;
  uid: string;
  resourceVersion: string;
}

export interface ManualWorkloadStrategy {
  type: string;
  paused: boolean;
  partition?: number;
}

export interface ManualWorkloadSnapshotResponse {
  version: 1;
  clusterId: string;
  generatedAt: string;
  target: ManualWorkloadTarget;
  currentReplicas: number;
  strategy: ManualWorkloadStrategy;
}

export interface ManualWorkloadActionRequest {
  version: 1;
  action: ManualWorkloadAction;
  target: ManualWorkloadTarget;
  parameters?: { replicas?: number };
}

export interface ManualWorkloadActionResponse {
  version: 1;
  action: ManualWorkloadAction;
  outcome: "scaled" | "restart_requested";
  target: ManualWorkloadTarget;
  observedReplicas: number;
  desiredReplicas?: number;
  requestedRestartAt?: string;
}

export interface ScopeResponse {
  /**
   * Output-only noncredential partition for bounded same-origin UI preferences.
   * It is never accepted on requests or used for authentication/authorization. (ADR 0066)
   */
  preferencePartition?: string;
  /** 응답 Pod의 capability일 뿐입니다. 모든 새 wire 응답은 각자 확인 응답이 필요합니다. */
  namespaceSelection?: NamespaceSelectionCapability;
  clusters: Array<{
    id: string;
    name: string;
    /** 이 클러스터에서 사용자가 접근 가능한 namespace. "all"이면 전체 */
    namespaces: string[] | "all";
    /** 접근 가능한 namespace가 하나도 없으면 false. 목록에는 보이되 선택 불가 */
    accessible: boolean;
    /**
     * namespaces가 "all"일 때 셀렉터 옵션으로 쓸 실제 namespace 이름 목록(서버
     * informer 캐시 열거). 표시 힌트일 뿐 권한 판정은 서버가 요청마다 강제합니다. (#1)
     */
    availableNamespaces?: string[];
  }>;
  /** Deployment/Secret 관리 탭·버튼 노출 여부(platform.admin + 관리 기능 활성). (ADR 0014) */
  canManageWorkloads?: boolean;
  /**
   * Resource Explorer 진입점 노출 여부. platform.admin에서 파생한 capability이면서
   * 이 배포에 direct 모드 resource 서비스가 있을 때만 true입니다. (ADR 0018)
   */
  canExploreResources?: boolean;
  /**
   * Resource Map 탭·진입점 노출 여부. canExploreResources와 이 배포의 RESOURCE_MAP_ENABLED
   * 게이트가 둘 다 참일 때만 true입니다 — 서버가 정본이며, 없거나 false면 UI는 Map 탭을
   * 만들지 않고 map 요청도 보내지 않습니다. (ADR 0020 · #8)
   */
  canUseResourceMap?: boolean;
  canUseAIChat?: boolean; canExecuteActions?: boolean; /* AI Chat Panel 진입점(ADR 0026) · Agent 제안 승인 컨트롤(ADR 0028). 서버가 정본이며 false면 UI는 진입점도 요청도 만들지 않습니다. 승인은 능력만 뜻하고 대상 범위는 clusters가 정합니다. */
  /**
   * 관리 그룹의 Debug Sessions 화면(전 사용자 세션 조회·강제 종료) 노출 여부. platform.admin에서
   * 파생하며 이 배포에 Debug Terminal 세션 레지스트리가 켜져 있을 때만 true입니다. 서버가 정본이며
   * false/없음이면 UI는 화면도 요청도 만들지 않습니다. (ADR 0030)
   */
  canManageExecSessions?: boolean;
  /** Argo source Scope 권한과 배포 기능 활성 상태를 모두 만족할 때만 true. (ADR 0072) */
  canViewGitOps?: boolean;
}

export interface NamespaceSelectionCapability {
  version: 1;
  maxMembers: 10;
  maxNameBytes: 63;
  maxNamesBytes: 630;
  maxTokensBytes: 660;
  maxBindingBytes: 669;
  maxHeadersBytes: 750;
}

/* ── Resource Explorer (ADR 0018) ───────────────────────────────────────────
   조회 전용입니다. 목록은 서버의 metadata informer 인덱스에서만 나오고,
   상세만 사용자가 항목을 연 순간의 격리된 live GET입니다. */

/** allowlist 한 항목의 상태. "0건"과 "권한 없음"과 "미지원"을 구분합니다. */
export type ResourceState = "ready" | "syncing" | "unsupported" | "forbidden" | "missing";

export interface ResourceDescriptor {
  /** core group은 "core"로 표기합니다. 경로 세그먼트가 비어 있을 수 없기 때문입니다. */
  group: string;
  version: string;
  resource: string;
  kind: string;
  namespaced: boolean;
  verbs: string[];
  /** 서버가 선호하는 group 버전(진단용). */
  preferredVersion?: string;
  state: ResourceState;
  /** ready가 아닐 때의 짧은 사유. 내부 주소·질의는 담기지 않습니다. */
  reason?: string;
  /** 로컬 인덱스에 담긴 객체 수(필터 전). */
  count: number;
  /**
   * 이 GVR에 변경 검토(dry-run)를 요청할 수 있는지. **권한이 아니라 배포 설정**이며,
   * 기능 스위치·opt-in 목록·hard-deny를 모두 통과할 때만 true입니다. (ADR 0019)
   * false면 UI는 검토 진입점을 만들지 않습니다.
   */
  dryRun?: boolean;
  /**
   * 이 GVR에 변경 적용을 요청할 수 있는지. dryRun과 같은 성격의 배포 capability이며
   * 권한이 아닙니다. **별도 opt-in**을 통과해야 true입니다. (ADR 0019 Phase 2)
   */
  apply?: boolean;
  /** 이 GVR에 삭제를 요청할 수 있는지. 적용과 별도 스위치입니다. */
  delete?: boolean;
  /**
   * 이 GVR의 전체 객체 인덱스 상태. 없으면 "off"와 같습니다 — 기본 배포가 그
   * 상태입니다. (ADR 0065 C2)
   */
  fullObject?: ResourceFullObjectMode;
  /** off가 아닐 때의 짧은 사유. 내부 주소·JSONPath 식은 담기지 않습니다. */
  fullObjectReason?: string;
  /**
   * 목록 행에 실리는 CRD printer column 이름(priority 0만).
   * 행의 `columns`와 **같은 순서**이며, 전체 객체 인덱스가 없으면 비어 있습니다.
   */
  columns?: string[];
}

/* ── CRD 정의 카탈로그 · 전체 객체 인덱스 · 관련 Event (ADR 0065) ───────────
   세 계약 모두 **조회 전용**입니다. 브라우저에서 allowlist를 바꾸거나 CRD를
   편집하는 경로는 없고, 전체 객체는 어떤 응답에도 실리지 않습니다 — printer
   column은 서버가 평가한 문자열 한 칸으로만 내려갑니다. */

/**
 * allowlist 한 항목의 전체 객체 인덱스 상태.
 *
 * `off`는 대상이 아님(기본값), `active`는 printer column이 목록에 실림,
 * `degraded`는 예산·정의 문제로 **metadata 모드로 떨어진** 상태입니다.
 * degraded에서도 목록·상세는 그대로 동작하고 열만 없습니다.
 */
export type ResourceFullObjectMode = "off" | "active" | "degraded";

/**
 * CRD 정의 목록 한 행의 Explorer allowlist 대조 결과. (ADR 0065 C1 결정 4)
 *
 * 세 상태를 구분합니다 — `known: false`는 "클러스터가 제공 버전을 아직 말해 주지
 * 않음"(설치 중·established 아님)이고, `known: true` + `allowlisted: false`는
 * "제공되지만 Explorer 대상이 아님"입니다. 둘을 같은 뱃지로 뭉개지 않습니다.
 */
export interface ResourceRowCRD {
  known: boolean;
  allowlisted: boolean;
  /** 선택된 GVR의 정규 표기(`group/version/plural`). */
  selectedGvr?: string;
  /**
   * 미포함일 때 운영자가 `resourceExplorer.extraResources`에 복사할 한 줄.
   * 포함되어 있으면 비어 있습니다 — 할 일이 없는데 복사 거리를 만들지 않습니다.
   */
  suggestedEntry?: string;
}

/**
 * `status.conditions` 한 줄. CRD 정의와 CR 인스턴스가 같은 모양을 씁니다.
 * 서버가 길이를 조이고 시각은 RFC3339로 다시 씁니다.
 */
export interface ResourceCondition {
  type: string;
  status?: string;
  reason?: string;
  lastTransitionTime?: string;
}

/** CRD가 선언한 printer column 한 줄의 **정의**. */
export interface ResourceCRDPrinterColumn {
  name: string;
  type: "string" | "integer" | "number" | "boolean" | "date";
  jsonPath: string;
  /** 0이면 목록, >0이면 상세입니다(kubectl·Freelens와 같은 규칙). */
  priority: number;
  description?: string;
}

export interface ResourceCRDVersion {
  name: string;
  served: boolean;
  storage: boolean;
  columns?: ResourceCRDPrinterColumn[];
}

export interface ResourceCRDNames {
  kind?: string;
  plural?: string;
  singular?: string;
  shortNames?: string[];
}

/**
 * CRD 정의 상세가 YAML과 **함께** 내려 주는 구조화 필드. (ADR 0065 C1 결정 3)
 *
 * **OpenAPI schema는 없습니다.** 의도한 생략입니다 — schema는 YAML 안에서만 보고,
 * 구조화하면 응답이 수 MiB가 됩니다.
 */
export interface ResourceCRDDefinition {
  group?: string;
  names: ResourceCRDNames;
  scope?: "Namespaced" | "Cluster";
  versions?: ResourceCRDVersion[];
  storedVersions?: string[];
  conditions?: ResourceCondition[];
  /**
   * 정의 본문이 말하는 선택 버전. 클러스터가 제공하는 preferred 버전과 다를 수
   * 있고, allowlist 대조는 `crdExplorer`가 말합니다.
   */
  selectedVersion?: string;
  truncated?: boolean;
}

/**
 * 상세에만 실리는 printer column 한 칸(priority > 0).
 * 값은 서버가 평가한 문자열 한 칸이며 96바이트를 넘지 않습니다.
 */
export interface ResourceDetailColumn {
  name: string;
  type?: "string" | "integer" | "number" | "boolean" | "date";
  value?: string;
}

/**
 * 관련 Event 절의 상태. (ADR 0065 C3)
 *
 * **"0건"과 "꺼짐"과 "인덱스 없음"을 구분합니다** — 셋을 같은 빈 목록으로 보여
 * 주면 운영자는 경고가 없다고 읽습니다. `ready` + 0건이 "경고 없음"입니다.
 */
export type ResourceRelatedEventState = "disabled" | "unavailable" | "forbidden" | "ready";

/**
 * 상세에 붙는 **Warning** Event 한 줄.
 * Normal Event는 어떤 경우에도 실리지 않습니다(ADR 0004).
 */
export interface ResourceRelatedEvent {
  id: string;
  type: "Warning";
  reason?: string;
  message?: string;
  count: number;
  lastSeen?: string;
}

export interface ResourceRelatedEvents {
  state: ResourceRelatedEventState;
  /** ready가 아닐 때의 짧은 사유. 내부 주소·질의는 담기지 않습니다. */
  reason?: string;
  items: ResourceRelatedEvent[];
  truncated?: boolean;
}

export interface ResourceCatalogResponse {
  clusterId: string;
  generatedAt: string;
  /** discovery snapshot을 마지막으로 만든 시각. */
  refreshedAt?: string;
  /** discovery 자체가 부분/전체 실패했는지. */
  degraded: boolean;
  reason?: string;
  items: ResourceDescriptor[];
}

/** 목록 한 줄. 신원은 이름이 아니라 UID입니다. */
export interface ResourceListItem {
  namespace?: string;
  name: string;
  uid: string;
  createdAt?: string;
  /**
   * CRD printer column(priority 0)의 **서버가 평가한** 값. 길이·순서는 응답의
   * `columns`와 같고, 전체 객체 인덱스가 없으면 비어 있습니다. (ADR 0065 C2 결정 3)
   */
  columns?: string[];
  /** 이 행이 **CRD 정의**일 때의 Explorer 대조 결과. (ADR 0065 C1 결정 4) */
  crd?: ResourceRowCRD;
}

export interface ResourceListResponse {
  clusterId: string;
  group: string;
  version: string;
  resource: string;
  kind: string;
  namespaced: boolean;
  generatedAt: string;
  /** 인덱스를 마지막으로 만든 시각. */
  observedAt?: string;
  appliedScope: { clusterId: string; namespaces: string[] | "all" };
  items: ResourceListItem[];
  /** opaque keyset cursor. offset 페이징은 어떤 요청에도 없습니다. (ADR 0003) */
  nextCursor?: string;
  /** 페이지·byte·scan 예산으로 조기 종료했는지. */
  truncated: boolean;
  /** 인덱스 전체 객체 수(필터 전). */
  total: number;
  /**
   * 이 페이지의 printer column 이름(priority 0). 행의 `columns`와 같은 순서이며,
   * 전체 객체 인덱스가 없으면 비어 있습니다. (ADR 0065 C2 결정 3)
   */
  columns?: string[];
}

export interface ResourceDetailResponse {
  clusterId: string;
  group: string;
  version: string;
  resource: string;
  apiVersion: string;
  kind: string;
  namespace?: string;
  name: string;
  uid: string;
  resourceVersion: string;
  generatedAt: string;
  /** 서버가 정제한 읽기 전용 YAML. Secret의 data/stringData는 들어 있지 않습니다. */
  yaml: string;
  /** 제거한 필드 경로. 가려졌다는 사실은 보이게 합니다. */
  redacted?: string[];

  /* ── ADR 0065 구조화 필드 ─────────────────────────────────────────────────
     전부 **정제가 끝난 객체**에서 서버가 뽑습니다. 전체 객체는 실리지 않습니다. */

  /** 열린 객체가 CRD 정의일 때의 구조화 필드. (C1 결정 3) */
  crd?: ResourceCRDDefinition;
  /** 그 정의가 가리키는 GVR의 Explorer 대조 결과. (C1 결정 4) */
  crdExplorer?: ResourceRowCRD;
  /** `status.conditions`를 구조화한 것. (C1 결정 3 · C2 결정 6) */
  conditions?: ResourceCondition[];
  /** priority > 0 printer column. 목록에는 실리지 않습니다. (C2 결정 5) */
  columns?: ResourceDetailColumn[];
  /** 기존 격리 GET으로 읽은 batch/v1 Job 한 건의 닫힌 투영. */
  job?: ResourceJobDetail;
  /** 여섯 builtin Kind의 선택적 닫힌 투영. 실패는 이 절에만 국소화됩니다. */
  builtin?: ResourceBuiltinDetail;
  /**
   * 이 객체에 붙은 Warning Event 절. **언제나 실립니다** — 꺼진 배포도 state로
   * 그 사실을 말합니다. (C3)
   */
  relatedEvents: ResourceRelatedEvents;
  /** 구조화 필드 중 하나가 상한 때문에 잘렸는지. */
  truncated?: boolean;
}

export type BoundedTextState = "ok" | "absent" | "withheld_too_large";

export interface BoundedText {
  state: BoundedTextState;
  value?: string;
  truncated?: boolean;
}

export type WorkloadConditionStatus = "True" | "False" | "Unknown";
export type WorkloadConditionType = "Available" | "Progressing" | "ReplicaFailure";

export interface WorkloadCondition {
  type: WorkloadConditionType;
  status: WorkloadConditionStatus;
  reason: BoundedText;
  message: BoundedText;
  lastTransitionTime?: string;
}

export interface WorkloadConditionSet {
  state: "supported" | "unsupported";
  items: WorkloadCondition[];
  omitted?: number;
}

export type ResourceJobConditionType = "Suspended" | "Complete" | "Failed" | "FailureTarget" | "SuccessCriteriaMet";

export interface ResourceJobCondition extends Omit<WorkloadCondition, "type"> {
  type: ResourceJobConditionType;
}

export interface ResourceJobConditionSet {
  state: "supported";
  items: ResourceJobCondition[];
  omitted?: number;
}

export interface ResourceJobDetail {
  parallelism?: number;
  completions?: number;
  conditions: ResourceJobConditionSet;
}

export type ResourceBuiltinProjectionState = "ok" | "unsupported" | "malformed" | "projection_too_large";
export type ResourceBuiltinValueState = "ok" | "absent" | "invalid" | "withheld_too_large";

export interface ResourceBuiltinValue {
  state: ResourceBuiltinValueState;
  value?: string;
  truncated?: boolean;
}

export interface ResourceBuiltinEnumValue<T extends string> extends Omit<ResourceBuiltinValue, "value"> {
  value?: T;
}

export interface ResourceBuiltinStringList {
  items: string[];
  omitted?: number;
  lowerBound?: boolean;
}

export interface ResourceBuiltinLink {
  group: string;
  version: string;
  resource: string;
  kind: string;
  namespace?: string;
  name: string;
  uid: string;
}

export interface ResourceBuiltinOwnerReference {
  apiVersion: ResourceBuiltinValue;
  kind: ResourceBuiltinValue;
  name: ResourceBuiltinValue;
  uid: ResourceBuiltinValue;
  controller?: boolean;
  link?: ResourceBuiltinLink;
}

export interface ResourceBuiltinOwnerReferenceList {
  items: ResourceBuiltinOwnerReference[];
  omitted?: number;
  lowerBound?: boolean;
}

export interface ResourceBuiltinMetadata {
  labelCount: number;
  annotationCount: number;
  labelKeys: ResourceBuiltinStringList;
  annotationKeys: ResourceBuiltinStringList;
  ownerReferences: ResourceBuiltinOwnerReferenceList;
}

export type ResourceBuiltinEndpointState = "not_indexed" | "count_unavailable";
export type ResourceBuiltinEndpointReason =
  | "syncing"
  | "forbidden"
  | "missing"
  | "unsupported"
  | "endpoint_ready_count_not_exposed";

export interface ResourceBuiltinEndpointReady {
  state: ResourceBuiltinEndpointState;
  reasonCode: ResourceBuiltinEndpointReason;
}

export interface ResourceBuiltinTargetPort {
  state: ResourceBuiltinValueState;
  type?: "number" | "name";
  number?: number;
  name?: string;
}

export interface ResourceBuiltinServicePort {
  name: ResourceBuiltinValue;
  port: number;
  targetPort: ResourceBuiltinTargetPort;
  protocol: ResourceBuiltinEnumValue<"TCP" | "UDP" | "SCTP">;
}

export interface ResourceBuiltinServicePortList {
  items: ResourceBuiltinServicePort[];
  omitted?: number;
  lowerBound?: boolean;
}

export interface ResourceBuiltinServiceDetail {
  type: ResourceBuiltinEnumValue<"ClusterIP" | "NodePort" | "LoadBalancer" | "ExternalName">;
  clusterIPs: ResourceBuiltinStringList;
  ports: ResourceBuiltinServicePortList;
  selectorPresent: boolean;
  externalName: ResourceBuiltinValue;
  endpointReady?: ResourceBuiltinEndpointReady;
}

export type ResourceBuiltinIngressBackendState = "service" | "unsupported_backend" | "invalid" | "absent";

export interface ResourceBuiltinIngressServicePort {
  state: ResourceBuiltinValueState;
  type?: "number" | "name";
  number?: number;
  name?: string;
}

export interface ResourceBuiltinIngressBackend {
  state: ResourceBuiltinIngressBackendState;
  serviceName: ResourceBuiltinValue;
  servicePort: ResourceBuiltinIngressServicePort;
  omitted?: number;
}

export interface ResourceBuiltinIngressPath {
  path: ResourceBuiltinValue;
  pathType: ResourceBuiltinEnumValue<"Exact" | "Prefix" | "ImplementationSpecific">;
  backend: ResourceBuiltinIngressBackend;
}

export interface ResourceBuiltinIngressPathList {
  items: ResourceBuiltinIngressPath[];
  omitted?: number;
  lowerBound?: boolean;
}

export interface ResourceBuiltinIngressRule {
  host: ResourceBuiltinValue;
  paths: ResourceBuiltinIngressPathList;
}

export interface ResourceBuiltinIngressRuleList {
  items: ResourceBuiltinIngressRule[];
  omitted?: number;
  lowerBound?: boolean;
}

export interface ResourceBuiltinIngressTLS {
  hosts: ResourceBuiltinStringList;
  secretName: ResourceBuiltinValue;
}

export interface ResourceBuiltinIngressTLSList {
  items: ResourceBuiltinIngressTLS[];
  omitted?: number;
  lowerBound?: boolean;
}

export interface ResourceBuiltinLoadBalancerIngress {
  ip: ResourceBuiltinValue;
  hostname: ResourceBuiltinValue;
}

export interface ResourceBuiltinLoadBalancerIngressList {
  items: ResourceBuiltinLoadBalancerIngress[];
  omitted?: number;
  lowerBound?: boolean;
}

export interface ResourceBuiltinIngressDetail {
  rules: ResourceBuiltinIngressRuleList;
  tls: ResourceBuiltinIngressTLSList;
  loadBalancer: ResourceBuiltinLoadBalancerIngressList;
}

export type ResourceBuiltinPVCConditionType =
  | "Resizing"
  | "FileSystemResizePending"
  | "ControllerResizeError"
  | "NodeResizeError"
  | "VolumeModifying"
  | "VolumeModifyVolumeError"
  | "VolumeModifyVolumeInUseError";
export type ResourceBuiltinConditionStatus = "True" | "False" | "Unknown";

export interface ResourceBuiltinPVCCondition {
  type: ResourceBuiltinPVCConditionType;
  status: ResourceBuiltinConditionStatus;
  reason: ResourceBuiltinValue;
  message: ResourceBuiltinValue;
  lastTransitionTime: ResourceBuiltinValue;
}

export interface ResourceBuiltinPVCConditionList {
  items: ResourceBuiltinPVCCondition[];
  omitted?: number;
  lowerBound?: boolean;
}

export interface ResourceBuiltinPVCMetric {
  state: "identity_unavailable";
  reasonCode: "generation_safe_identity_not_proven";
}

export interface ResourceBuiltinPVCDetail {
  phase: ResourceBuiltinEnumValue<"Pending" | "Bound" | "Lost">;
  storageClassName: ResourceBuiltinValue;
  accessModes: ResourceBuiltinStringList;
  requestedStorage: ResourceBuiltinValue;
  capacityStorage: ResourceBuiltinValue;
  volumeName: ResourceBuiltinValue;
  conditions: ResourceBuiltinPVCConditionList;
  metric: ResourceBuiltinPVCMetric;
}

export interface ResourceBuiltinPVClaimRef {
  namespace: ResourceBuiltinValue;
  name: ResourceBuiltinValue;
  uid: ResourceBuiltinValue;
}

export interface ResourceBuiltinPVDetail {
  phase: ResourceBuiltinEnumValue<"Pending" | "Available" | "Bound" | "Released" | "Failed">;
  capacityStorage: ResourceBuiltinValue;
  reclaimPolicy: ResourceBuiltinEnumValue<"Retain" | "Recycle" | "Delete">;
  storageClassName: ResourceBuiltinValue;
  claimRef?: ResourceBuiltinPVClaimRef;
}

export interface ResourceBuiltinStorageClassDetail {
  provisioner: ResourceBuiltinValue;
  reclaimPolicy: ResourceBuiltinEnumValue<"Retain" | "Recycle" | "Delete">;
  volumeBindingMode: ResourceBuiltinEnumValue<"Immediate" | "WaitForFirstConsumer">;
  isDefault: boolean;
}

export interface ResourceBuiltinServiceAccountDetail {
  secrets: ResourceBuiltinStringList;
  imagePullSecrets: ResourceBuiltinStringList;
  automountServiceAccountToken?: boolean;
}

export interface ResourceBuiltinDetail {
  state: ResourceBuiltinProjectionState;
  metadata?: ResourceBuiltinMetadata;
  service?: ResourceBuiltinServiceDetail;
  ingress?: ResourceBuiltinIngressDetail;
  persistentVolumeClaim?: ResourceBuiltinPVCDetail;
  persistentVolume?: ResourceBuiltinPVDetail;
  storageClass?: ResourceBuiltinStorageClassDetail;
  serviceAccount?: ResourceBuiltinServiceAccountDetail;
}

/* ── 전역 리소스 검색 (ADR 0023) ─────────────────────────────────────────────
   ADR 0018 metadata 인덱스 위의 조회 전용 접두사 검색입니다.
   Scope는 후보 순회 전에 적용되므로 권한 밖 객체는 truncated·cursor에도
   영향을 주지 않습니다. namespace 파라미터가 없는 것이 계약입니다. */

/** 결과가 걸린 필드. 색이 아니라 텍스트로 함께 표시합니다. */
export type ResourceMatchField = "name" | "namespace" | "kind" | "label";

/**
 * 검색 결과 한 줄.
 *
 * **status 필드는 없습니다** — 검색은 PartialObjectMetadata 인덱스에서만 읽고
 * 그 안에 status가 없으므로, 있는 척하는 필드를 계약에 만들지 않습니다.
 */
export interface ResourceSearchItem {
  group: string;
  version: string;
  resource: string;
  kind: string;
  namespaced: boolean;
  namespace?: string;
  name: string;
  uid: string;
  matchedField: ResourceMatchField;
}

export interface ResourceSearchResponse {
  clusterId: string;
  query: string;
  generatedAt: string;
  /** 인덱스를 마지막으로 만든 시각. */
  observedAt?: string;
  appliedScope: { clusterId: string; namespaces: string[] | "all" };
  items: ResourceSearchItem[];
  /** 질의어와 Scope에 묶인 opaque keyset cursor. */
  nextCursor?: string;
  /** 페이지·byte·scan 예산으로 조기 종료했는지. Scope 밖 객체는 영향을 주지 않습니다. */
  truncated: boolean;
  /** 색인 예산으로 일부 리소스나 label이 빠졌는지. 잘린 검색을 완전한 검색처럼 보여주지 않습니다. */
  degraded: boolean;
  reason?: string;
}

/** 서버가 다시 확인해 준 최근 항목. 제목의 근거는 전부 서버 값입니다. */
export interface ResourceRecentItem {
  group: string;
  version: string;
  resource: string;
  kind: string;
  namespaced: boolean;
  namespace?: string;
  name: string;
  uid: string;
}

/**
 * 재해석된 최근 항목. 요청 순서를 그대로 지킵니다.
 *
 * 참조는 compact base64url이며 요청당 최대 20개, 참조 하나는 1024자, query string
 * 전체는 8KiB가 상한입니다. 웹은 6KiB에서 요청을 나누고 하나의 취소 신호를
 * 공유하며 입력 순서대로 합칩니다. 해석되지 않은 참조는 오류가 아니라 목록에서 빠집니다.
 */
export interface ResourceRecentResponse {
  clusterId: string;
  generatedAt: string;
  appliedScope: { clusterId: string; namespaces: string[] | "all" };
  items: ResourceRecentItem[];
}

/* 변경 검토·적용·삭제(ADR 0019)의 타입·상수는 별도 모듈입니다.
   이 파일이 1,000줄을 넘어 새 범주를 안에 두면 어느 타입이 어느 결정에 속하는지
   읽을 수 없기 때문입니다. 소비자는 지금까지처럼 패키지 루트에서 import합니다. */
export * from "./resource-change";

/* ── Resource Map (ADR 0020 · #8) ────────────────────────────────────────────
   Resources 경계 아래의 조회 전용 관계 그래프 계약입니다.

   정본 경로: GET /api/v1/clusters/{clusterId}/resources/{group}/{version}/{resource}/map

   raw Kubernetes 객체·spec·status·data·stringData·env·라벨 값은 어떤 필드로도
   나가지 않습니다 — evidence.field는 경로/키 이름뿐이고 값이 아닙니다. 어휘는
   서버 코어(resourcecatalog) enum과 글자 단위로 같습니다.

   상한(서버 강제, 요청이 넓힐 수 없음): depth 기본 2·최대 4, 노드 500, 간선
   1500, 관계 필터 16개, query string 8KiB, name·uid·namespace와 합성 키 각각
   1024바이트, 응답 인코딩 1MiB(초과는 502 object_too_large). */

/** 그래프 간선 종류의 닫힌 어휘. 요청 관계 필터도 이 값만 받습니다. */
export type ResourceMapRelationType =
  | "owner"
  | "selects"
  | "routesTo"
  | "mounts"
  | "scales"
  | "protects"
  | "storage"
  | "serviceAccount";

/** 이 배포에서 그 관계를 계산할 수 있는지. "0건"과 "지원 안 함"을 구분합니다. */
export type ResourceMapRelationState = "supported" | "partial" | "unsupported";

/**
 * 노드의 캐시 전용 건강(닫힌 집합). 투영 시점의 informer 캐시 status에서 도출한
 * 값이며 요청 경로에서 다시 계산하지 않습니다. 규칙이 없는 Kind·미해석 노드는
 * "unknown"이고 "정상"과 다릅니다. Severity 어휘의 부분집합이라 StatusBadge에 그대로 쓸 수 있습니다.
 */
export type ResourceMapNodeHealth = "healthy" | "warning" | "critical" | "unknown";

export type ResourceMapEvidenceKind =
  | "ownerReference"
  | "label"
  | "selector"
  | "targetRef"
  | "namedRef";

export type ResourceMapTruncationReason =
  | "depth"
  | "nodes"
  | "edges"
  | "bytes"
  | "work"
  | "deadline"
  | "fanout";

/** 간선의 근거. field는 경로/키 이름이고 **값이 아닙니다** — 라벨 근거도 키만 남습니다. */
export interface ResourceMapEvidence {
  kind: ResourceMapEvidenceKind;
  field: string;
}

/** 응답 노드 하나. 라벨·spec·status 원문·값 필드는 없습니다. */
export interface ResourceMapNode {
  /** 응답 안에서 노드를 가리키는 opaque 키(간선 from/to의 대상). */
  key: string;
  /** 해석된 노드에만 있습니다. */
  uid?: string;
  /** core group은 "core"로 표기합니다. */
  group: string;
  version: string;
  kind: string;
  /** 비어 있으면 cluster-scoped입니다. */
  namespace?: string;
  name: string;
  depth: number;
  /** false면 "참조는 있으나 캐시에 없음"이며, 존재하지 않음과 다릅니다. */
  resolved: boolean;
  /** 캐시 전용 건강. 항상 닫힌 집합의 값이며 미해석이면 "unknown"입니다. */
  health: ResourceMapNodeHealth;
}

/** 방향 있는 간선 하나. A→B와 B→A는 서로 다른 간선입니다. */
export interface ResourceMapEdge {
  from: string;
  to: string;
  type: ResourceMapRelationType;
  resolved: boolean;
  evidence: ResourceMapEvidence;
}

export interface ResourceMapRelationSupport {
  type: ResourceMapRelationType;
  state: ResourceMapRelationState;
  reason?: string;
}

/** 색인이 없어 만들지 않은 관계. 예산에 걸린 절단(truncation)과 다른 사실입니다. */
export interface ResourceMapOmission {
  node: string;
  type: ResourceMapRelationType;
  reason: string;
}

/** 상한 때문에 무엇이 얼마나 빠졌는지. Scope 때문에 제외된 것은 세지 않습니다. */
export interface ResourceMapTruncation {
  reasons: ResourceMapTruncationReason[];
  omittedNodes: number;
  omittedEdges: number;
  /** 관계 종류별 생략 간선 수. 키는 관계 어휘로 닫혀 있습니다. */
  byRelation: Partial<Record<ResourceMapRelationType, number>>;
  /** true면 생략 수는 정확한 값이 아니라 하한입니다. */
  lowerBound: boolean;
}

/**
 * focus 하나에서 시작한 유계 관계 그래프.
 *
 * 배열 순서는 서버의 결정적 순서이고, 빈 배열·맵은 null이 아니라 []·{}입니다.
 * appliedScope는 서버가 강제한 Scope이며 질의에서 파생되지 않습니다.
 */
export interface ResourceMapResponse {
  clusterId: string;
  generatedAt: string;
  /** 그래프 스냅숏을 만든 시각. 항상 유효한 RFC3339이며 생략되지 않습니다. */
  observedAt: string;
  appliedScope: { clusterId: string; namespaces: string[] | "all" };
  focus: ResourceMapNode;
  /** 실제 적용된 깊이. 서버 절대 상한은 maxDepth입니다. */
  depth: number;
  maxDepth: number;
  nodes: ResourceMapNode[];
  edges: ResourceMapEdge[];
  relations: ResourceMapRelationSupport[];
  omissions: ResourceMapOmission[];
  truncated: boolean;
  truncation: ResourceMapTruncation;
}

export type AuthSessionResponse =
  | { authenticated: false }
  | { authenticated: false; refreshable: true; csrfToken: string }
  | {
      authenticated: true;
      principal: { displayName: string };
      capabilities: { canEditDashboard: boolean; canPublishDashboard: boolean };
      expiresAt: string;
      refreshAt: string;
      csrfToken: string;
    };

export interface DashboardCapabilities { enabled:boolean; canEdit:boolean; canPublish:boolean; maxDrafts:number; maxWidgets:number; }
export type DashboardDraftState = "draft" | "submitted" | "approved";
export interface DashboardDraft { id:string; revision:number; state:DashboardDraftState; owned:boolean; schemaVersion:1; definition:DashboardDefinition; createdAt:string; updatedAt:string; }
export interface DashboardDraftPage { items:DashboardDraft[]; nextCursor?:string; }
export interface DashboardDefinitionRequest { definition:DashboardDefinition; }

/**
 * 화면 전체가 실패했을 때만 쓰는 최상위 에러. 섹션 단위 실패는 Section으로 표현합니다.
 *
 * **호환 별칭입니다** — 정본 어휘·봉투는 APIErrorCode/APIError 하나이고, 이 이름은
 * 기존 import를 깨지 않기 위해 남아 있습니다. 과거에는 여기에 code union이 중복
 * 정의되어 있어 서버가 실제로 내는 코드(scope_too_broad·query_rejected·query_timeout
 * 등)가 빠진 채 갈라졌습니다. 새 코드는 APIErrorCode에만 추가합니다.
 */
export type ApiError = APIError;

/** GET /version 응답. 값은 빌드 시 주입되며 로컬 기본값은 dev/unknown입니다. */
export interface VersionInfo {
  version: string;
  commit: string;
  buildDate: string;
}

/* ══════════════════════════════════════════════════════════════════════════
   Drill-down — 이슈 #15
   Cluster → Namespace → Workload → Pod → Container
   화면 하나당 요청 하나 원칙(ADR 0002)을 그대로 따릅니다.
   ══════════════════════════════════════════════════════════════════════════ */

/** 리소스 사용량과 Request/Limit 대비 비율. 비율은 서버가 계산해 내려줍니다. */
export interface ResourceUsage {
  /** CPU millicore */
  cpuMilli: number;
  cpuRequestMilli: number;
  cpuLimitMilli: number | null;
  /** Memory MiB */
  memoryMib: number;
  memoryRequestMib: number;
  memoryLimitMib: number | null;
  /** 0~1 이상이면 과사용. limit이 없으면 null */
  cpuVsRequest: number;
  cpuVsLimit: number | null;
  memoryVsRequest: number;
  memoryVsLimit: number | null;
}

export type WorkloadKind = "Deployment" | "StatefulSet" | "DaemonSet" | "ReplicaSet" | "CronJob";

/** Cluster Overview에서 정규화한 상태 어휘를 그대로 씁니다. */
export type PodPhase = "Running" | "Pending" | "Succeeded" | "Failed" | "Unknown";

/** 필터 칩에 쓰이는 정규화 사유. 서버가 정규화해 내려줍니다. */
export type IssueReason =
  | "CrashLoopBackOff"
  | "ImagePullBackOff"
  | "Pending"
  | "ReplicaMismatch"
  | "RolloutStalled"
  | "Restarting"
  | "OOMKilled"
  | "ProbeFailed";

/** Nodes 화면의 노드별 Pod 행. 신원은 UID입니다. */
export interface NodePodSummary {
  uid: string;
  name: string;
  namespace: string;
  phase: string;
  severity: Severity;
  restarts: number;
  cpuRequestMilli: number;
  memoryRequestMib: number;
}

export interface NodeCapacity {
  cpuMilli: number;
  memoryMib: number;
  pods: number;
}

export interface NodeRequested {
  cpuMilli: number;
  memoryMib: number;
}

/**
 * Nodes 화면의 노드 하나 요약. requested/limits와 pods 목록은 스케줄러 관점
 * (종료되지 않은 Pod 전체)입니다. 실측 사용량은 메트릭 데이터소스 몫입니다.
 */
export interface NodeSummary {
  name: string;
  roles: string[];
  ready: boolean;
  unschedulable: boolean;
  pressure: boolean;
  severity: Severity;
  kubeletVersion: string;
  osImage: string;
  internalIP: string;
  ageSeconds: number;
  capacity: NodeCapacity;
  allocatable: NodeCapacity;
  requested: NodeRequested;
  limits: NodeRequested;
  podsTotal: number;
  pods: NodePodSummary[];
}

/** Nodes 화면 응답. 화면 하나 = 요청 하나. (ADR 0002) */
export interface NodeListResponse {
  clusterId: string;
  generatedAt: string;
  nodes: Section<NodeSummary[]>;
}

/* ── 노드 경량 목록·상세 (#207) ───────────────────────────────────────────── */

/** 배열을 자른 사실. 자르지 않았으면 total만 채워집니다. */
export interface NodeTruncation {
  total: number;
  omitted: number;
  truncated: boolean;
}

/**
 * 배포가 선언한 **정확 키 allowlist**로 투영된 노드 라벨. 기본은 비어 있어
 * 응답에 아예 실리지 않습니다. 원문 라벨 map·annotation은 나가지 않습니다.
 */
export interface NodeLabel {
  key: string;
  value: string;
}

/**
 * `?view=summary` 목록 행. 노드별 Pod 행이 **없습니다** — 목록에서 Pod 전체를
 * 싣는 것이 레거시 응답이 커진 원인입니다. 신원은 이름이 아니라 uid +
 * creationTimestamp입니다.
 */
export interface NodeSummaryView {
  name: string;
  uid: string;
  creationTimestamp: string;
  roles: string[];
  ready: boolean;
  unschedulable: boolean;
  pressure: boolean;
  severity: Severity;
  kubeletVersion: string;
  internalIP: string;
  ageSeconds: number;
  allocatable: NodeCapacity;
  requested: NodeRequested;
  podsTotal: number;
  /**
   * 분모는 **allocatable**입니다. 분모가 0이거나 없으면 0이 아니라 `null`입니다 —
   * "요청이 없음"과 "비율을 계산할 수 없음"은 다른 사실입니다.
   */
  cpuRequestRatio: number | null;
  memoryRequestRatio: number | null;
  rolesTruncated?: boolean;
  labels?: NodeLabel[];
}

export interface NodeSummaryList {
  nodes: NodeSummaryView[];
  truncation: NodeTruncation;
  /** 노드 수 상한이 아니라 **바이트 상한** 때문에 잘렸음입니다. */
  bytesTruncated?: boolean;
}

/** `?view=summary` 응답. 노드 500개 · 512 KiB로 유계입니다. */
export interface NodeSummaryListResponse {
  clusterId: string;
  generatedAt: string;
  view: "summary";
  nodes: Section<NodeSummaryList>;
}

export interface NodeCondition {
  type: string;
  status: string;
  severity: Severity;
  reason?: string;
  message?: string;
  lastTransition?: string;
}

/** Taint. **value는 싣지 않습니다** — 운영자가 넣은 임의 문자열입니다. */
export interface NodeTaint {
  key: string;
  effect: string;
}

export interface NodeAddress {
  type: string;
  address: string;
}

export interface NodeDetailTruncation {
  conditions: NodeTruncation;
  taints: NodeTruncation;
  addresses: NodeTruncation;
  roles: NodeTruncation;
}

/** 노드 하나의 캐시 전용 상태. 원문 라벨·annotation·spec은 없습니다. */
export interface NodeDetail {
  name: string;
  uid: string;
  creationTimestamp: string;
  ageSeconds: number;
  roles: string[];
  ready: boolean;
  unschedulable: boolean;
  pressure: boolean;
  severity: Severity;
  kubeletVersion: string;
  kubeProxyVersion?: string;
  osImage: string;
  containerRuntime?: string;
  kernelVersion?: string;
  architecture?: string;
  operatingSystem?: string;
  internalIP: string;
  capacity: NodeCapacity;
  allocatable: NodeCapacity;
  requested: NodeRequested;
  limits: NodeRequested;
  cpuRequestRatio: number | null;
  memoryRequestRatio: number | null;
  podsTotal: number;
  conditions: NodeCondition[];
  taints: NodeTaint[];
  addresses: NodeAddress[];
  labels?: NodeLabel[];
  truncation: NodeDetailTruncation;
}

export interface NodeDetailPods {
  pods: NodePodSummary[];
  truncation: NodeTruncation;
  bytesTruncated?: boolean;
}

/**
 * 노드 시리즈 하나의 **닫힌** 상태.
 * - `ok`       가드를 통과한 값이 있습니다.
 * - `warming`  노드가 방금 만들어져 아직 신뢰할 수 있는 샘플이 없습니다.
 * - `missing`  원천에 이 노드의 시리즈가 없습니다.
 * - `degraded` 조회·검증이 실패했습니다. 값을 추측해 채우지 않습니다.
 */
export type NodeTelemetryState = "ok" | "warming" | "missing" | "degraded";

/**
 * UI가 그릴 시리즈의 **닫힌 집합**. 서버는 표시 이름을 보내지 않습니다 — 보내면 그
 * 문자열이 서버의 언어로 고정되어 en 모드에서도 한국어가 나옵니다. 키만 받고 문구는
 * UI 카탈로그가 고릅니다.
 */
export type NodeTelemetrySeriesKey = "cpu.used" | "memory.used" | "memory.total";

/**
 * ok가 아닌 상태의 **닫힌 사유 코드**. 사람이 읽는 문장이 아닙니다 — 서버가 문장을
 * 보내면 표시 언어를 서버가 정하게 되고 내부 주소가 새어 나갈 자리도 생깁니다.
 */
export type NodeTelemetryReason =
  | "warming_rate_window"
  | "warming_no_post_birth_sample"
  | "series_absent"
  | "no_sample_in_range"
  | "no_common_evaluation"
  | "upstream_unavailable"
  | "upstream_invalid"
  | "query_unsupported"
  | "identity_ambiguous"
  | "identity_mismatch"
  | "pair_broken"
  | "point_budget_exceeded"
  | "cross_family_identity_mismatch"
  | "dependency_degraded";

export interface NodeTelemetrySeries {
  key: NodeTelemetrySeriesKey;
  unit: MetricUnit;
  state: NodeTelemetryState;
  /** ok가 아닐 때의 닫힌 사유. ok면 생략됩니다. */
  reasonCode?: NodeTelemetryReason;
  /** warming일 때 값이 신뢰 가능해지는 시각. */
  readyAt?: string;
  points: TrendPoint[];
}

/** Step은 서버가 범위로 강제하고 UI는 표시만 합니다. (ADR 0001) */
export interface NodeTelemetry {
  stepSeconds: number;
  series: NodeTelemetrySeries[];
}

/**
 * 노드 상세 응답. 화면 하나 = 요청 하나. (ADR 0002)
 * 세 섹션이 독립이라 메트릭 원천이 죽어도 노드 상태와 Pod 목록은 값을 유지합니다.
 */
export interface NodeDetailResponse {
  clusterId: string;
  generatedAt: string;
  range: { key: RangeKey; from: string; to: string; stepSeconds: number };
  nodeName: string;
  node: Section<NodeDetail>;
  pods: Section<NodeDetailPods>;
  telemetry: Section<NodeTelemetry>;
}

export interface NamespaceSummary {
  name: string;
  severity: Severity;
  workloads: { total: number; unhealthy: number };
  pods: { total: number; running: number; pending: number; failed: number; restarts: number };
  usage: ResourceUsage;
  /** 이 Namespace에서 관측된 문제 사유. 목록 화면 필터에 씁니다. */
  issues: IssueReason[];
}

export interface NamespaceListResponse {
  clusterId: string;
  range: { key: RangeKey; from: string; to: string; stepSeconds: number };
  generatedAt: string;
  namespaces: Section<NamespaceSummary[]>;
}

export interface WorkloadSummary {
  ref: EntityRef;
  name: string;
  kind: WorkloadKind;
  namespace: string;
  severity: Severity;
  /** DaemonSet은 desired가 노드 수입니다. */
  replicas: { desired: number; ready: number; available: number; updated: number };
  rollout: { status: "Complete" | "Progressing" | "Stalled" | "Paused"; message?: string };
  restarts: number;
  usage: ResourceUsage;
  images: string[];
  issues: IssueReason[];
  ageSeconds: number;
}

export interface NamespaceDetailResponse {
  clusterId: string;
  namespace: string;
  range: { key: RangeKey; from: string; to: string; stepSeconds: number };
  generatedAt: string;
  summary: Section<NamespaceSummary>;
  workloads: Section<WorkloadSummary[]>;
  trends: Section<TrendPanel[]>;
  events: Section<ClusterEvent[]>;
}

/** OwnerReference 체인. Deployment → ReplicaSet → Pod 관계를 그대로 표현합니다. */
export interface OwnerRef {
  kind: WorkloadKind | "Node" | "Job";
  name: string;
  uid: string;
  /** 현재 활성 ReplicaSet인지. 롤아웃 중에는 여러 개가 공존합니다. */
  current?: boolean;
  /** 이 소유자에 속한 Pod 수 */
  pods?: number;
  revision?: string;
}

/** Workloads 화면(#215)의 화면 단위 단일 응답 계약입니다. */
export type WorkloadsOverviewKind = "Deployment" | "StatefulSet" | "DaemonSet" | "CronJob";

export interface WorkloadKindSummary {
  kind: WorkloadsOverviewKind;
  total: number;
  healthy: number;
  warning: number;
  critical: number;
  restarts:
    | { value: number; state: "exact" }
    | { value: null; state: "unavailable"; reason: "owner_chain_unsupported" };
  issues: Array<{ reason: IssueReason; workloads: number }>;
}

export interface WorkloadsOverviewPodRow {
  uid: string;
  name: string;
  namespace: string;
  phase: string;
  severity: Severity;
  ready: string;
  restarts: number;
  node: BoundedText;
  qosClass: "Guaranteed" | "Burstable" | "BestEffort" | "Unknown";
  primaryIP: BoundedText;
  issues: IssueReason[];
  /** top owner에서 direct owner 순서입니다(Deployment → ReplicaSet). */
  ownerChain: OwnerRef[];
  ownerChainComplete: boolean;
  ownerChainReason?: "owner_chain_unsupported" | "owner_identity_mismatch";
  resolvedWorkloadKind?: WorkloadsOverviewKind;
  resolvedWorkloadName?: string;
  resolvedWorkloadUid?: string;
}

export interface WorkloadsOverviewPodList {
  rows: WorkloadsOverviewPodRow[];
  total: number;
  shown: number;
  omitted: number;
  truncated: boolean;
  coverage: "exact";
}

export interface WorkloadsOverviewResponse {
  clusterId: string;
  generatedAt: string;
  appliedScope: { clusterId: string; namespaces: string[] | "all" };
  filters: { q: string };
  limits: {
    maxQueryBytes: 128;
    maxPodRows: 500;
    maxRawPods: 100000;
    maxRawWorkloads: 20000;
    maxResponseBytes: 1048576;
  };
  workloads: Section<WorkloadKindSummary[]>;
  pods: Section<WorkloadsOverviewPodList>;
}

export interface ContainerStatus {
  name: string;
  image: string;
  /** 이미지 태그가 아니라 실제 배포된 digest. 롤백 판단에 필요합니다. */
  imageId?: string;
  ready: boolean;
  started: boolean;
  restarts: number;
  state: "Running" | "Waiting" | "Terminated";
  /** Waiting/Terminated일 때의 사유. CrashLoopBackOff 등 */
  reason?: string;
  message?: string;
  lastTerminated?: { reason: string; exitCode: number; finishedAt: string };
  usage?: { cpuMilli: number; memoryMib: number };
  probes: { liveness: "passing" | "failing" | "none"; readiness: "passing" | "failing" | "none" };
}

export interface PodSummary {
  ref: EntityRef;
  name: string;
  /** Pod UID. 이름이 같아도 재생성되면 다른 인스턴스입니다. (이슈 #15 완료 기준) */
  uid: string;
  namespace: string;
  phase: PodPhase;
  severity: Severity;
  ready: string;
  restarts: number;
  node: string;
  /** 소유 체인의 직접 부모(대개 ReplicaSet) */
  owner?: OwnerRef;
  issues: IssueReason[];
  usage: ResourceUsage;
  startedAt: string;
  /** 이미 삭제된 인스턴스면 종료 시각이 있습니다. */
  finishedAt?: string;
}

export interface WorkloadDetailResponse {
  clusterId: string;
  namespace: string;
  range: { key: RangeKey; from: string; to: string; stepSeconds: number };
  generatedAt: string;
  workload: Section<WorkloadSummary>;
  /** direct informer cache의 닫힌 spec/status 투영. central은 degraded/unsupported입니다. */
  structured: Section<WorkloadStructure>;
  /** Deployment → ReplicaSet 체인. DaemonSet/StatefulSet은 비어 있습니다. */
  ownerChain: Section<OwnerRef[]>;
  pods: Section<PodSummary[]>;
  trends: Section<TrendPanel[]>;
  events: Section<ClusterEvent[]>;
  /**
   * HTTP 요청 SLO / Error Budget (ADR 0051 · #157).
   * 기능이 꺼진 배포에서는 `degraded` + `slo_not_configured`로 내려오고
   * 상류 호출은 0회입니다. 이 섹션이 미상이어도 다른 섹션은 값을 유지합니다.
   */
  slo: Section<SloSummary>;
  /**
   * 보존 리비전 앵커 전후 HTTP 요청 비교 (ADR 0053 · #158).
   * 기능이 꺼진 배포에서는 `degraded` + `compare_not_configured`로 내려오고
   * **비교의 상류 호출은 0회**입니다(켜져 있는 SLO 섹션은 영향받지 않습니다).
   * 이 섹션이 미상이어도 다른 섹션은 값을 유지합니다.
   */
  rolloutCompare: Section<RolloutCompare>;
  /**
   * 저장 지표 기준선 대비 이동 조사 (ADR 0055 · #159).
   * 기능이 꺼진 배포에서는 `degraded` + `investigation_not_configured`, 켜졌지만
   * `?investigation=errorRatioShift`로 요청하지 않으면 `investigation_not_requested`로
   * 내려오고 **조사의 상류 호출은 0회**입니다. 이 섹션이 미상이어도 다른 섹션은 값을 유지합니다.
   */
  metricInvestigation: Section<MetricInvestigation>;
}

export interface WorkloadStrategyValue {
  state: "ok" | "unavailable";
  value?: string;
}

export interface WorkloadStrategy {
  type: "Recreate" | "RollingUpdate" | "OnDelete";
  maxSurge?: WorkloadStrategyValue;
  maxUnavailable?: WorkloadStrategyValue;
}

export interface WorkloadReplicaSet {
  name: string;
  uid: string;
  revision?: string;
  revisionState: "valid" | "unknown";
  createdAt?: string;
  current: boolean;
  pods: number;
}

export interface WorkloadReplicaSetInventory {
  rows: WorkloadReplicaSet[];
  shown: number;
  total: number;
  truncated: boolean;
  scanTruncated: boolean;
}

export interface WorkloadCronJobDetail {
  schedule: BoundedText;
  suspended: boolean;
  lastScheduleTime?: string;
  lastSuccessfulTime?: string;
  jobHistory: "job_history_unavailable";
}

export interface WorkloadStructure {
  strategy?: WorkloadStrategy;
  conditions: WorkloadConditionSet;
  replicaSets?: WorkloadReplicaSetInventory;
  cronJob?: WorkloadCronJobDetail;
}

export type PodArrayLimitReason = "item_limit" | "byte_limit";

export interface PodArraySummary {
  shown: number;
  total: number;
  omitted: number;
  truncated: boolean;
  reason?: PodArrayLimitReason;
}

export interface PodTextInventory extends PodArraySummary {
  items: BoundedText[];
}

export interface PodToleration {
  key: BoundedText;
  operator: "Exists" | "Equal" | "Unknown";
  effect: "NoSchedule" | "PreferNoSchedule" | "NoExecute" | "Unknown";
  tolerationSeconds?: number;
}

export interface PodTolerationInventory extends PodArraySummary {
  items: PodToleration[];
}

export interface PodContainerPort {
  name: BoundedText;
  containerPort: number;
  protocol: "TCP" | "UDP" | "SCTP" | "Unknown";
}

export interface PodContainerPortInventory extends PodArraySummary {
  items: PodContainerPort[];
}

export interface PodVolumeMount {
  name: BoundedText;
  mountPath: BoundedText;
  readOnly: boolean;
  subPathPresent: boolean;
}

export interface PodVolumeMountInventory extends PodArraySummary {
  items: PodVolumeMount[];
}

export interface PodResourceQuantity {
  cpu: BoundedText;
  memory: BoundedText;
  ephemeralStorage: BoundedText;
}

export interface PodContainerSpec {
  kind: "app" | "init" | "ephemeral";
  name: BoundedText;
  ports: PodContainerPortInventory;
  volumeMounts: PodVolumeMountInventory;
  resources: { requests: PodResourceQuantity; limits: PodResourceQuantity };
}

export interface PodContainerInventory {
  items: PodContainerSpec[];
  summary: PodArraySummary;
  app: PodArraySummary;
  init: PodArraySummary;
  ephemeral: PodArraySummary;
}

export type PodVolumeType =
  | "persistentVolumeClaim" | "configMap" | "secret" | "projected" | "downwardAPI"
  | "emptyDir" | "csi" | "ephemeral" | "hostPath" | "other";

export interface PodVolume {
  name: BoundedText;
  type: PodVolumeType;
  referenceName?: BoundedText;
  hostPathPresent?: boolean;
}

export interface PodVolumeInventory extends PodArraySummary {
  items: PodVolume[];
}

export interface PodStructure {
  serviceAccountName: BoundedText;
  qosClass: "Guaranteed" | "Burstable" | "BestEffort" | "Unknown";
  priorityClassName: BoundedText;
  nodeName: BoundedText;
  podIPs: PodTextInventory;
  tolerations: PodTolerationInventory;
  affinity: { nodeAffinity: boolean; podAffinity: boolean; podAntiAffinity: boolean };
  topologySpreadCount: number;
  containers: PodContainerInventory;
  volumes: PodVolumeInventory;
}

export type ContainerMetricState = "ok" | "warming" | "missing" | "degraded";
export type ContainerMetricReason =
  | "not_running" | "rate_window" | "series_absent"
  | "sample_before_pod" | "sample_future" | "sample_stale"
  | "cpu_layout_unsupported" | "cpu_layout_ambiguous"
  | "identity_ambiguous" | "identity_mismatch" | "identity_malformed"
  | "pair_broken" | "point_budget_exceeded"
  | "upstream_unavailable" | "upstream_invalid" | "result_too_large" | "query_unsupported";

/** 원시 원천 신원 라벨은 의도적으로 이 공개 타입에 포함하지 않습니다. */
export interface ContainerMetricSample {
  state: ContainerMetricState;
  /** 유효한 숫자 0을 포함해 ok일 때 존재합니다. */
  value?: number;
  sampleTime?: string;
  sampleAgeSeconds?: number;
  reasonCode?: ContainerMetricReason;
}

export interface ContainerMetric {
  kind: "app" | "init" | "ephemeral";
  name: string;
  cpu: ContainerMetricSample;
  memory: ContainerMetricSample;
}

export interface ContainerMetricSnapshot {
  evaluatedAt: string;
  cpuWindowSeconds: 300;
  memoryFreshnessSeconds: 300;
  containers: ContainerMetric[];
}

export interface PodDetailResponse {
  clusterId: string;
  namespace: string;
  range: { key: RangeKey; from: string; to: string; stepSeconds: number };
  generatedAt: string;
  pod: Section<PodSummary>;
  /** direct informer cache의 닫힌 Pod spec/status 투영. central은 degraded/unsupported입니다. */
  structured: Section<PodStructure>;
  /** 선택된 전역 history range와 무관한 현재 컨테이너 CPU 5분 rate/메모리 snapshot입니다. */
  containerMetrics: Section<ContainerMetricSnapshot>;
  /** Pod → ReplicaSet → Deployment 순서의 상위 체인 */
  ownerChain: Section<OwnerRef[]>;
  containers: Section<ContainerStatus[]>;
  trends: Section<TrendPanel[]>;
  events: Section<ClusterEvent[]>;
  /** 이 Pod가 참조하는 Secret 이름(값 아님). Secret 조회 카드에 씁니다. (#33) */
  secretRefs: string[];
}

export const ISSUE_LABEL: Record<IssueReason, string> = {
  CrashLoopBackOff: "CrashLoopBackOff",
  ImagePullBackOff: "ImagePullBackOff",
  Pending: "Pending",
  ReplicaMismatch: "Replica 불일치",
  RolloutStalled: "Rollout 지연",
  Restarting: "재시작 발생",
  OOMKilled: "OOMKilled",
  ProbeFailed: "Probe 실패",
};

/* ══════════════════════════════════════════════════════════════════════════
   Logs Explorer & 상관분석 — 이슈 #16
   ══════════════════════════════════════════════════════════════════════════ */

export type LogLevel = "ERROR" | "WARN" | "INFO" | "DEBUG";

/** 마스킹된 구간. UI가 원문을 복원할 수 없도록 위치와 종류만 내려옵니다. */
export interface MaskedSpan {
  /** message 내 시작 offset */
  start: number;
  length: number;
  kind: "token" | "password" | "secret" | "email" | "ip" | "card";
}

export interface LogLine {
  /** 정렬·커서에 쓰는 안정적 식별자 */
  id: string;
  /** epoch milliseconds */
  t: number;
  level: LogLevel;
  /** 이미 마스킹된 본문. 원문은 서버 밖으로 나가지 않습니다. (README §10) */
  message: string;
  /** 어디가 가려졌는지. UI가 표시만 하고 복원하지는 않습니다. */
  masked: MaskedSpan[];
  namespace: string;
  podName: string;
  podUid: string;
  containerName: string;
  workloadKind?: WorkloadKind;
  workloadName?: string;
  nodeName?: string;
  traceId?: string;
  spanId?: string;
  /** 구조화 로그의 부가 필드 */
  attributes?: Record<string, string>;
}

/**
 * Cursor 기반 페이징.
 * Quickwit의 search-after와 같은 방식입니다. offset을 쓰지 않는 이유는
 * 로그가 계속 들어오는 동안 offset이 밀려 **중복·누락**이 생기기 때문입니다.
 * 커서는 (timestamp, id) 복합키를 불투명 문자열로 인코딩한 값입니다.
 */
export interface LogCursor {
  /** 다음 페이지 요청에 그대로 넣습니다. null이면 더 없음 */
  next: string | null;
  /** 서버가 적용한 페이지 크기 */
  pageSize: number;
}

export interface LogSearchRequest {
  clusterId: string;
  namespace?: string;
  workloadName?: string;
  podUid?: string;
  containerName?: string;
  levels?: LogLevel[];
  /** 전문 검색어. Raw Query가 아니라 서버가 이스케이프해 처리합니다. */
  q?: string;
  from: string;
  to: string;
  cursor?: string;
}

export interface LogSearchResponse {
  lines: Section<LogLine[]>;
  cursor: LogCursor;
  /** 시간 범위 전체의 레벨별 분포. 히스토그램과 필터 배지에 씁니다. */
  histogram: Section<Array<{ t: number; counts: Record<LogLevel, number> }>>;
  /** 같은 Scope·시간 범위의 Kubernetes Event. 로그와 같은 타임라인에 겹칩니다. */
  events: Section<ClusterEvent[]>;
  /** 필터 드롭다운을 채우는 값들. 현재 Scope에서 실제로 관측된 것만 내려옵니다. */
  facets: Section<{
    workloads: Array<{ name: string; kind: WorkloadKind; count: number }>;
    pods: Array<{ name: string; uid: string; count: number }>;
    containers: Array<{ name: string; count: number }>;
  }>;
  /** 서버가 적용한 Scope와 범위. UI가 보낸 값과 다를 수 있습니다. */
  applied: {
    clusterId: string;
    namespace: string | null;
    from: string;
    to: string;
    /** 결과 상한에 걸렸는지. 걸렸다면 화면에 명시합니다. */
    truncated: boolean;
    maxLines: number;
  };
  generatedAt: string;
}

export const LEVEL_ORDER: LogLevel[] = ["ERROR", "WARN", "INFO", "DEBUG"];

export const MASK_LABEL: Record<MaskedSpan["kind"], string> = {
  token: "토큰",
  password: "비밀번호",
  secret: "시크릿",
  email: "이메일",
  ip: "IP",
  card: "카드번호",
};

/* ══════════════════════════════════════════════════════════════════════════
   Pod Topology — 이슈 #16 후속 (design-system의 topology 컴포넌트를 앱에 연결)
   ══════════════════════════════════════════════════════════════════════════ */

export type Protocol = "HTTP" | "gRPC" | "TCP" | "UDP";

/** 토폴로지 그래프의 원천. 없으면 데모 등 전체 관측을 가정합니다. */
/**
 * 그래프의 원천. `traces`는 계측된 호출만(ADR 0033), `structure`는 Kubernetes 구조가 만든
 * 도달 경로, `mixed`는 둘이 한 그래프에 있다는 뜻입니다. (ADR 0037)
 */
export type TopologySource = "traces" | "structure" | "mixed";

/**
 * 진입점 선언을 클러스터가 실제로 성립시켰는지. (ADR 0044)
 *
 * 원천은 진입점 **자신의 status**입니다 — Gateway 객체는 읽지 않고, `parentRef`의
 * 신원도 `allowedRoutes` 판정도 응답에 실리지 않습니다. **`unknown`은 "정상"이
 * 아닙니다** — 판정 원천이 없거나(Ingress는 언제나 이 값), 조건이 모호하거나,
 * 상한을 넘겼다는 뜻입니다. 화면은 넷을 구분해 표기합니다.
 */
export type TopologyEntryAdmission = "unknown" | "admitted" | "partial" | "rejected";

export interface TopologyNode {
  id: string;
  ref: EntityRef;
  name: string;
  namespace: string;
  severity: Severity;
  /** 레이아웃 열(0부터). 서버가 의존 방향으로 위상 정렬해 내려줍니다. */
  column: number;
  row: number;
  /** 클러스터 밖 엔티티(Ingress Gateway·External Client 등). Pod 상세로 연결하지 않습니다. (#29) */
  external?: boolean;
  /** 이 노드로 접힌 Pod 수. 노드는 워크로드 단위이므로 접힘 규모를 표시합니다. (#3) */
  podCount?: number;
  /**
   * 이 노드가 가리키는 대상 종류(Deployment·StatefulSet·DaemonSet·Route 등).
   * 구조 그래프는 워크로드가 아닌 노드(진입점·static Pod)도 담습니다. (ADR 0037)
   */
  kind?: string;
  /**
   * 닿는 엣지가 하나도 없는 노드. 화면은 기본으로 접고 수만 보입니다 —
   * 빼는 것이 아니라 접는 것이라 무엇이 빠졌는지 셀 수 있어야 합니다. (ADR 0037)
   */
  isolated?: boolean;
  /**
   * 진입점 선언이 클러스터에서 성립했는지. **`unknown`도 문자열로 내려옵니다** —
   * 필드가 비는 것의 뜻은 "이 노드는 진입점이 아니다" 하나뿐입니다. (ADR 0044 결정 7)
   */
  entryAdmission?: TopologyEntryAdmission;
}

export interface TopologyRoute {
  protocol: Protocol;
  /** HTTP/gRPC는 API 경로, TCP/UDP는 라우트 식별자 */
  route: string;
  /** 선택된 시간 범위 누적 요청 수 */
  count: number;
  errorCount: number;
}

export interface TopologyEdge {
  id: string;
  from: string;
  to: string;
  severity: Severity;
  /** 시간 범위 전체 누적. 초당 환산은 UI가 계산합니다. */
  totalCount: number;
  errorRate: number;
  protocols: Protocol[];
  routes: TopologyRoute[];
  /**
   * 이 선이 무엇을 말하는지. 구조는 "부를 수 있다", 실측은 "불렀다"입니다.
   * 화면은 둘을 다른 모양으로 그립니다 — 이 구분을 지우면 ADR 0037을 되돌리는 것입니다.
   */
  origin?: TopologyEdgeOrigin;
  /**
   * 구조 엣지가 지나는 Service 이름. 같은 두 노드 사이 경로가 여럿이면 여기 모입니다 —
   * 선을 여러 겹으로 긋지 않습니다. (ADR 0037 결정 4)
   */
  via?: string[];
  /**
   * 구조 엣지가 지나는 경로 — 어떤 호스트로 들어와 어떤 경로·포트를 거쳐 닿는지,
   * 그 끝의 엔드포인트가 몇 개 Ready인지. 실측 엣지에는 없습니다. (ADR 0040)
   */
  paths?: TopologyPath[];
  /**
   * 상한을 넘어 싣지 못한 경로 수. 0이 아니면 화면이 그 수를 밝힙니다 —
   * 조용한 절단을 만들지 않습니다. (ADR 0040 결정 7)
   */
  pathsOmitted?: number;
}

/**
 * 구조 엣지가 지나는 경로 하나. (ADR 0040)
 *
 * 구조 엣지 하나가 `진입점 → Service`와 `Service → 워크로드` **두 홉**을 함께 덮으므로
 * (ADR 0037 결정 4) 포트가 둘입니다. 하나로 합치면 화면이 어느 홉의 수인지 말할 수 없습니다.
 * (ADR 0040 결정 2)
 *
 * host·path는 라우팅 **선언**이라 브라우저로 옵니다. TLS 인증서·secretName은 라우팅
 * 선언이 아니라 자료이므로 이 계약에 없습니다. (ADR 0040 결정 3)
 */
export interface TopologyPath {
  /** 이 경로가 지나는 Service 이름. */
  service: string;
  /** 진입점이 선언한 호스트. 비면 제한이 없습니다. */
  host?: string;
  /** 진입점이 선언한 경로. 비면 제한이 없습니다. */
  path?: string;
  /**
   * path의 매칭 종류("Exact"·"Prefix"·"RegularExpression"·"ImplementationSpecific").
   * 비면 선언이 없는 것이고, Prefix로 추정하지 않습니다.
   */
  pathMatch?: string;
  /** 진입점 → Service 홉의 포트("http"·"8080"·"http:8080"). */
  servicePort?: string;
  /**
   * Service → 워크로드 홉의 포트. 이 경로가 닿는 EndpointSlice가 서로 다른 포트를 둘
   * 이상 선언하면 비어 있습니다 — 어느 것이 이 경로의 포트인지는 추정하지 않습니다.
   */
  endpointPort?: string;
  /**
   * 그 서로 다른 엔드포인트 포트의 수. 0이면 선언이 없는 것이고, 2 이상이면
   * endpointPort가 비어 있는 이유입니다.
   */
  endpointPorts?: number;
  /**
   * EndpointSlice `ports[].port`가 nil이었다는 뜻 — **0이 아니라 "포트 제한 없음"**입니다.
   * (ADR 0040 결정 2)
   */
  endpointPortUnrestricted?: boolean;
  /**
   * "TCP"·"UDP". SCTP는 계약 어휘(Protocol)에 없어 비어 옵니다 — 없는 값을 TCP로
   * 추정하지 않습니다. (ADR 0040 결정 2)
   */
  protocol?: Protocol;
  /** 이 경로가 닿는 엔드포인트 중 Ready인 수. */
  ready: number;
  /** Ready가 아닌 수. */
  notReady: number;
  /** `conditions.ready`가 nil이라 **모르는** 수. "정상"이 아닙니다. (ADR 0040 결정 6) */
  unknown: number;
}

/** 엣지가 어디서 왔는지. (ADR 0037) */
export type TopologyEdgeOrigin = "structure" | "observed" | "both";

/** 상한에 걸려 그리지 못한 수. 0이 아니면 화면이 그 수를 밝힙니다. (ADR 0037) */
export interface TopologyOmitted {
  nodes?: number;
  edges?: number;
}

/**
 * 실측(트레이스)이 닿은 워크로드가 몇 개인지. (ADR 0039 결정 4)
 *
 * 분모는 범례가 이미 내는 "워크로드 노드 N개"와 **같은 모집단**이고(`external`이 아닌 노드),
 * 분자는 실측 엣지가 닿은 노드 중 같은 조건을 만족하는 것입니다. 실측 어댑터가 상한에 걸린
 * 엣지를 세지 않고 버리므로 분자는 언제나 하한이며, 정확한 비율로 말하지 않습니다. (결정 6(a))
 */
export interface TopologyCoverage {
  /** 분모 — 이 그래프의 워크로드 노드 수. */
  workloads: number;
  /** 분자 — 실측 호출이 닿은 워크로드 수. 0은 "계측 0"이고, 필드 부재와는 다른 사실입니다. */
  instrumented: number;
}

/**
 * NetworkPolicy가 걸린 namespace가 몇 개인지. (ADR 0042)
 *
 * **정책은 엣지가 아닙니다**(결정 1) — 허용 정책은 호출이 아니고, 색인에 들어오는 것은
 * metadata뿐이라 `podSelector`도 규칙도 없습니다. 그래서 주어는 워크로드가 아니라
 * **namespace**이고(결정 2), 자리도 노드 배지가 아니라 그래프 단위 필드입니다(결정 3).
 * 정책의 이름·규칙·포트·CIDR은 내려오지 않습니다 — 수뿐입니다(결정 5).
 *
 * **"정책이 있다"는 "막혀 있다"가 아닙니다.** 존재는 상태가 아니므로 상태 색을 쓰지
 * 않습니다. (결정 7)
 */
export interface TopologyNetworkPolicies {
  /** 분모 — Scope가 확정한 namespace 수. 색인에 객체가 없는 namespace도 "정책 0개"로 남습니다. (결정 6) */
  namespaces: number;
  /** NetworkPolicy가 하나 이상 있는 namespace 수. 0은 "하나도 없다"는 사실입니다. */
  withPolicy: number;
  /** 그 namespace들의 NetworkPolicy 객체 총수. 노드 상한과 무관합니다. (결정 6) */
  policies: number;
}

/**
 * 외부 진입점(Ingress·HTTPRoute·Route)이 몇 개이고, 이 배포가 어떤 종류를 색인하는지. (ADR 0040 · #125)
 *
 * 진입점이 0건이면 구조가 서 있어도 **밖에서 들어오는 선이 하나도 생기지 않습니다.**
 * 그림만으로는 "구조는 섰는데 진입점이 없다"와 "구조가 잘 돌고 있다"가 구분되지 않으므로
 * 화면은 이 값으로 그 사유를 말합니다. **"구조가 아예 없다"는 여기서 말하지 않습니다** —
 * 그쪽은 `sources`에 `structure`가 빠지는 것으로 이미 표현됩니다.
 *
 * 진입점의 이름·호스트는 내려오지 않습니다 — 그 값은 노드와 엣지의 경로 레코드가 나릅니다.
 */
export interface TopologyEntryPoints {
  /** Scope 안에서 본 진입점 수. 노드 상한과 무관하고, 0은 사실이라 필드 부재와 다릅니다. */
  count: number;
  /**
   * 이 배포가 색인하는 진입점 종류. 비어 있으면 `count` 0은 "없다"가 아니라 **"셀 수 없다"**입니다 —
   * HTTPRoute·Route는 CRD라 `resourceExplorer` opt-in이 있어야 색인에 들어옵니다.
   */
  kinds: string[];
  /**
   * 그중 수용되지 않은 진입점 수 — `partial`·`rejected`만 셉니다. `unknown`은
   * 미수용으로 단정하지 않습니다. `count`와 같은 근거로 0도 언제나 내려옵니다.
   * (ADR 0044 결정 3-1·7)
   */
  unaccepted: number;
}

/** 관리자가 저장한 노드 좌표. (#28) */
export interface TopologyNodePosition {
  id: string;
  x: number;
  y: number;
}

/** 모든 사용자가 공유하는 저장 배치. null이면 기본 배치를 씁니다. */
export interface TopologyLayout {
  positions: TopologyNodePosition[];
  updatedAt: string;
}

export interface TopologyResponse {
  clusterId: string;
  namespace: string | null;
  range: { key: RangeKey; from: string; to: string; stepSeconds: number };
  generatedAt: string;
  pods: Section<{ total: number; healthy: number; unhealthy: number; unhealthyList: UnhealthyEntity[] }>;
  /** source="traces"면 트레이스로 계측된 서비스 기준 그래프입니다(ADR 0033). */
  graph: Section<{
    nodes: TopologyNode[];
    edges: TopologyEdge[];
    source?: TopologySource;
    /** 실제로 기여한 원천들. "구조는 있는데 계측이 없다"는 단일 값으로 말할 수 없습니다. */
    sources?: TopologySource[];
    omitted?: TopologyOmitted;
    /** 닿는 엣지가 없어 화면이 기본으로 접는 노드 수. (ADR 0037 결정 9) */
    isolatedNodes?: number;
    /**
     * 계측 커버리지. **구조 그래프가 없으면 분모를 알 수 없어 필드 자체가 없습니다** —
     * 0으로 내려오면 "계측 0%"라는 거짓이 됩니다. (ADR 0039 결정 6(b))
     */
    coverage?: TopologyCoverage;
    /**
     * NetworkPolicy 존재 — **namespace 단위**입니다. 구조가 기여했고 이 배포가 정책을
     * 색인할 때만 내려옵니다. (ADR 0042 결정 2·4)
     */
    networkPolicies?: TopologyNetworkPolicies;
    /**
     * 이 배포가 NetworkPolicy를 색인하지 않는다는 뜻(`resourceExplorer.resources`에서
     * 뺀 배포). "정책 0개"와 같은 화면으로 처리하지 않습니다. (ADR 0042 결정 4)
     */
    networkPoliciesNotIndexed?: boolean;
    /**
     * 외부 진입점 집계. **구조가 기여했을 때만** 내려옵니다 — 구조가 없으면 0이
     * "진입점이 없다"가 아니라 "구조가 없다"이고, 그것은 `sources`가 이미 말합니다.
     * (ADR 0040 · #125)
     */
    entryPoints?: TopologyEntryPoints;
  }>;
  layout: TopologyLayout | null;
  canEditLayout: boolean;
  /**
   * 저장된 좌표 중 이 응답의 그래프 노드와 실제로 맞는 수. graph 섹션이 ok일 때만
   * 셉니다 — degraded·empty·forbidden에서는 0이고, 화면은 upstream 장애를 "낡은
   * 배치"로 바꿔 말하지 않습니다. positions가 있는데 0이면 저장된 배치가 이 화면에
   * 하나도 적용되지 않았다는 뜻입니다. (#123)
   */
  layoutApplied: number;
  /** 배치 저장소를 읽지 못했다는 뜻. "저장 없음"과 같은 화면으로 처리하지 않습니다. (#123) */
  layoutUnavailable: boolean;
}

/** 엣지 하나의 시계열. 라인 클릭 후 차트로 전환할 때만 별도 조회합니다. */
export interface TopologyEdgeSeriesResponse {
  edgeId: string;
  range: { key: RangeKey; from: string; to: string; stepSeconds: number };
  generatedAt: string;
  series: Section<TrendSeries[]>;
}

/* ══════════════════════════════════════════════════════════════════════════
   Alerts — 이슈 #17
   자체 평가 엔진을 만들지 않습니다. Grafana Alerting / Alertmanager의 상태를
   공통 모델로 정규화해 조회만 합니다.
   ══════════════════════════════════════════════════════════════════════════ */

export type AlertSeverity = "critical" | "warning" | "info";
export type AlertStatus = "firing" | "resolved" | "pending";

export interface AlertInstance {
  /** 정규화된 fingerprint. 백엔드가 달라도 같은 알림은 같은 id를 갖습니다. */
  id: string;
  name: string;
  severity: AlertSeverity;
  status: AlertStatus;
  /**
   * 시작 시각은 **알 수 없을 수 있습니다**(ADR 0049 결정 8). Grafana annotation의
   * 시각은 전이 관측 시각이지 최초 발화 시각이 아니므로 이력에서는 비어 있습니다.
   * 없을 때 지속시간을 계산하지 않습니다 — 지어낸 값이 사실을 덮습니다.
   */
  startsAt?: string;
  endsAt?: string;
  labels: Record<string, string>;
  annotations: Record<string, string>;
  /** label에서 유추한 Unified Entity Model 참조. 못 찾으면 undefined. */
  entity?: EntityRef;
  entityName?: string;
  /** 원본 시스템 상세 화면. 새 탭으로 엽니다. */
  sourceUrl?: string;
  source: "grafana" | "alertmanager";
  /** 같은 그룹으로 묶인 인스턴스 수. 1이면 단독. */
  groupSize: number;
  /** 그룹 키 — 무엇을 기준으로 묶었는지 화면에 노출합니다. */
  groupKey: string;
}

/**
 * 알림 화면 Section이 degraded일 때의 **닫힌 사유 집합**입니다(ADR 0050 D7).
 *
 * 서버는 문장이 아니라 이 코드를 보냅니다 — 문구는 ko/en 카탈로그가 고릅니다.
 * 구성의 사실(`*_not_configured`·`history_scope_unsupported`·`history_scope_too_large`)은
 * 장애가 아니라서 출처가 없고, 나머지는 실제 상류 장애입니다.
 * `multiple_upstreams_unavailable`은 **양쪽이 모두 실제 장애일 때만** 나옵니다.
 */
export type AlertSectionReason =
  | "history_not_configured"
  | "history_scope_unsupported"
  | "history_scope_too_large"
  | "alerts_not_configured"
  | "upstream_unavailable"
  | "timeout"
  | "circuit_open"
  | "invalid_response"
  | "result_too_large"
  | "multiple_upstreams_unavailable";

/** 해소 이력 조회의 절단 사유. 둘 다 해당하면 upstream_raw_cap이 우선합니다. */
export type AlertHistoryTruncation = "upstream_raw_cap" | "display_cap";

/**
 * 질의 결과의 커버리지입니다(ADR 0049 결정 5). **보존기간이나 원천 이력의 완전성을
 * 증명하지 않습니다** — 이 조회가 무엇을 덮었는지만 말합니다.
 */
export type AlertHistoryCoverage = "complete" | "bounded" | "filtered";

/**
 * 해소 이력 조회의 상한·건수·커버리지입니다. 독립 Section이 **아니라 평문 필드**이고
 * degraded 사유는 `resolved` Section이 전달합니다. 조회가 실제로 일어났을 때만 있습니다.
 */
export interface AlertHistoryMeta {
  source: "grafana";
  /** 고정 상수 1000. 설정 키가 없습니다. */
  rawCap: number;
  /** 고정 상수 200. 설정 키가 없습니다. */
  displayCap: number;
  rawReturned: number;
  accepted: number;
  /** 화면에 실린 부분집합의 건수입니다. 창 전체 합계가 아닙니다. */
  returned: number;
  truncated: boolean;
  truncationReason?: AlertHistoryTruncation;
  queryCoverage: AlertHistoryCoverage;
  /** Grafana 조회가 성공했을 때만 true입니다. */
  resolvedCountsAvailable: boolean;
  /** queryCoverage !== "complete" 와 같습니다. */
  resolvedCountsPartial: boolean;
  droppedUnscoped: number;
  droppedState: number;
  droppedInvalidTime: number;
}

/**
 * 심각도 하나의 건수입니다. 두 필드 모두 **optional**입니다 — 발화와 해소는 서로
 * 다른 원천이라 실패한 쪽을 0으로 지어내면 "알림이 없다"는 거짓말이 됩니다.
 * 속성이 없으면 "조회 실패", 0이면 "조회 성공, 0건"입니다.
 */
export interface AlertCount {
  firing?: number;
  resolved?: number;
}

export interface AlertListResponse {
  clusterId: string;
  range: { key: RangeKey; from: string; to: string; stepSeconds: number };
  generatedAt: string;
  /** 진행 중 알림. Alert backend가 죽으면 degraded로 내려오고 화면은 계속 동작합니다. */
  firing: Section<AlertInstance[]>;
  /** 선택 범위 안에서 해소된 알림 */
  resolved: Section<AlertInstance[]>;
  counts: Section<Record<AlertSeverity, AlertCount>>;
  /** 중복 grouping 기준. 화면에 그대로 노출해 "왜 묶였는지"를 설명합니다. */
  groupingRule: string;
  /** 이력 조회가 일어났을 때만 실립니다. 미구성·Scope 초과·실패면 없습니다. */
  history?: AlertHistoryMeta;
}

export const ALERT_SEVERITY_LABEL: Record<AlertSeverity, string> = {
  critical: "Critical",
  warning: "Warning",
  info: "Info",
};

export const ALERT_STATUS_LABEL: Record<AlertStatus, string> = {
  firing: "진행 중",
  resolved: "해소됨",
  pending: "평가 중",
};

/* ── 진단 위젯 (k8sdiag ADR-0001) ────────────────────────────────────────── */

/**
 * `GET /api/v1/k8sdiag/capability` 응답입니다. 패널을 띄울지 판정하는 주체는
 * 진단 서비스이며, 화면은 `available`이 거짓이면 패널을 **렌더하지 않습니다** —
 * 비활성 버튼으로 남기지 않습니다. `reason`은 문장이 아니라 고정 코드입니다.
 *
 * 선택 쿼리 `namespace`(RFC 1123 label, 63자 이하)를 실으면 판정이 그 하나로
 * 좁혀집니다 — 화면이 보고 있는 네임스페이스를 진단할 수 없으면 패널이 숨습니다.
 * 생략하면 인가 집합 전체로 판정하는 종전 동작입니다.
 */
export interface K8sdiagCapability {
  available: boolean;
  tools: string[];
  exec_enabled: boolean;
  reason: "no_namespace_grant" | "exec_requires_user_identity" | null;
  /** 이 빌드가 광고하는 고정 소개 문구입니다. 배포 구성을 담지 않습니다. (ADR-0005 §6) */
  description?: string;
  sample_questions?: string[];
}

/**
 * 화면 하나의 좌표입니다 (k8sdiag ADR-0005 §1). 질문과 따로 `PUT /v1/viewer`로
 * 등록하며, `page_context`와 같은 취급을 받습니다 — 인가가 아니라 힌트이고
 * 인가 집합 밖이면 상류가 조용히 버립니다. `cluster` 화면은 그 아래 객체가 없어
 * `namespace`가 없습니다.
 */
export interface K8sdiagFocus {
  kind: "namespace" | "workload" | "pod" | "cluster";
  cluster_id?: string;
  namespace?: string;
  workload_kind?: string;
  name?: string;
}

/** `GET /api/v1/k8sdiag/sessions`의 한 줄. 본인 대화만 돌아옵니다. */
export interface K8sdiagSession {
  session_id: string;
  host: string;
  updated_at: number;
  question_preview: string;
  /** 거짓이면 권한 범위가 좁아져 이 대화를 복원할 수 없습니다. 없으면 참입니다. */
  replayable?: boolean;
}

export interface K8sdiagTurn {
  role: "user" | "assistant";
  content: string;
}

export interface K8sdiagSessionTurns {
  session_id: string;
  /** 거짓이면 권한 범위가 좁아져 이 대화를 복원할 수 없습니다. 없으면 참입니다. */
  replayable?: boolean;
  turns: K8sdiagTurn[];
}

export interface K8sdiagModel {
  id: string;
  label: string;
}

export interface K8sdiagModels {
  models: K8sdiagModel[];
  default: string;
}

/**
 * 진단 요청에 실리는 화면 맥락입니다. 라우트 파라미터에서 유도하며 사용자가
 * 입력하는 값이 아닙니다. 인가는 토큰에서만 나오고 이 값은 힌트입니다.
 * 열거의 소유자는 진단 서비스이며 임의 필드를 덧붙이면 요청 전체가 422입니다.
 */
export interface K8sdiagPageContext {
  kind: "namespace" | "workload" | "pod";
  cluster_id?: string;
  namespace: string;
  workload_kind?: string;
  name?: string;
}

/**
 * 진단 스트림 이벤트. `message` 또는 `error` 뒤에 스트림이 닫힙니다.
 *
 * `context_used`는 첫 `tool_call` 앞에 한 번 나오고, 값은 **프롬프트가 실제로 실은
 * 좌표**입니다 — 인가 집합 밖이라 버려졌으면 `null`입니다. 버려진 좌표를 되비추면
 * 화면이 서버가 하지 않은 일을 했다고 말하게 됩니다. (ADR-0005 §3)
 */
export type K8sdiagEvent =
  | {
      event: "context_used";
      data: { focus: K8sdiagFocus | null; scope?: string[]; namespace_count?: number; is_admin?: boolean };
    }
  | { event: "tool_call"; data: { tool: string; arguments: Record<string, unknown>; scope: string[] } }
  | { event: "tool_result"; data: { tool: string; bytes: number; preview: string; truncated: boolean } }
  | { event: "message"; data: { answer: string } }
  | { event: "error"; data: { error: string } };

/* ── 진단 에이전트 (ADR 0043 결정 3 · ADR 0045) ──────────────────────────── */

/**
 * `GET /api/v1/agents`의 한 줄입니다.
 *
 * 목록은 두 소유자의 답을 **ID로 대조한 교집합**입니다 — manager가 이 사용자에게
 * 보여 주는 Agent 가운데 `options.observer_diagnose`가 참이고, agent control
 * plane에서 실행 중인 ReAct 채팅 런타임인 것뿐입니다. 상류 주소(`service_url`)는
 * 여기 오지 않습니다: 호출 대상은 운영자가 설정한 URL 하나로 고정입니다.
 */
export interface AgentSummary {
  id: string;
  name: string;
  description?: string;
}

export interface AgentList {
  agents: AgentSummary[];
}

/**
 * 에이전트에게 넘기는 화면 좌표입니다. k8sdiag의 힌트와 달리 **서버가 Scope로
 * 검증**하고, 통과하지 못하면 403입니다 — 상류는 이 저장소의 Scope를 모릅니다.
 * 화면 공유가 꺼져 있으면 이 값이 아예 없습니다.
 */
export interface AgentFocus {
  kind: "cluster" | "namespace" | "workload" | "pod";
  cluster_id?: string;
  namespace?: string;
  workload_kind?: string;
  name?: string;
}

/**
 * `POST /api/v1/agents/{id}/diagnose/stream`의 본문입니다.
 *
 * 넘어가는 것은 셋뿐입니다 — 검증된 좌표, 원래 k8sdiag 세션 식별자, 사용자가 쓴
 * 질문. 답변 본문과 도구 결과 미리보기는 넘어가지 않습니다: 마스킹된 문자열을
 * 다른 저장소로 복사하는 경로를 만들지 않습니다. (ADR 0043 결정 2)
 */
export interface AgentDiagnoseRequest {
  question: string;
  /** 이어지는 질문의 **에이전트 대화** 식별자입니다. k8sdiag 세션과 다른 값입니다. */
  session_id?: string | null;
  k8sdiag_session_id?: string | null;
  focus?: AgentFocus;
}

/**
 * 에이전트 스트림 이벤트입니다. (ADR 0045 결정 6)
 *
 * k8sdiag의 `tool_call`·`tool_result`·`message`와 **같은 이름을 쓰지 않습니다.**
 * 상류에는 "이만큼 읽었다"는 도구 결과가 없고, 답변은 전문이 아니라 조각으로
 * 옵니다. 없는 사실을 이름만 맞춰 옮기면 화면이 서버가 하지 않은 일을 말합니다.
 *
 * - `message_delta`는 **조각**입니다. 받는 쪽이 이어 붙입니다.
 * - `tool_progress`는 실행의 시작·끝이며 `id`가 신원입니다 — 도구는 병렬로 돌 수
 *   있으므로 마지막 항목에 완료를 붙이면 다른 도구의 완료가 됩니다.
 * - `approval_required`는 **사람의 승인 요청**이 왔다는 사실이고, 이 패널에 승인
 *   경로가 없다는 표시입니다. 실행된 도구 결과가 아닙니다.
 * - `done`은 상류가 `[DONE]`을 낸 뒤에만 나가는 **완결 표식**입니다. 연결이
 *   닫혔다는 사실만으로는 "다 왔다"와 "중간에 끊겼다"를 구별할 수 없습니다.
 */
export type AgentDiagnoseEvent =
  | { event: "context_used"; data: { focus: AgentFocus | null; source: "agent" } }
  | { event: "done"; data: Record<string, never> }
  | { event: "message_delta"; data: { delta: string } }
  | {
      event: "tool_progress";
      data: { id: string; name: string; kind?: string; phase: string; status?: string; elapsed_ms?: number };
    }
  | { event: "approval_required"; data: { supported: false } }
  | { event: "error"; data: { error: string } };

/** `GET /api/v1/agents/{id}/sessions`의 한 줄. 본인 대화만 돌아옵니다. */
export interface AgentSessionSummary {
  session_id: string;
  title?: string;
  preview?: string;
  updated_at?: string;
}

export interface AgentSessionList {
  sessions: AgentSessionSummary[];
  next_cursor?: string;
}

export interface AgentTurn {
  role: "user" | "assistant";
  content: string;
}

export interface AgentSessionTurns {
  session_id: string;
  turns: AgentTurn[];
}

export * from "./stream-events"; /* 하위 계약 재수출 — SSE(#12) · 로그(ADR 0021) · Exec(ADR 0022) */
export * from "./live-logs"; export * from "./exec-session"; export * from "./ai-chat"; export * from "./agent-action"; export * from "./agent-action-execution"; /* 로그·Exec·AI 채팅·Action 제안·승인 실행(ADR 0021·0022·0026·0027·0028) */
export * from "./extension-capabilities"; /* 확장 능력(ADR 0024) */
export * from "./slo"; /* 워크로드 HTTP 요청 SLO / Error Budget(ADR 0051 · #157) */
export * from "./rollout-compare"; /* 보존 리비전 앵커 전후 비교(ADR 0053 · #158) */
export * from "./metric-investigation"; /* 저장 지표 기준선 대비 이동 조사(ADR 0055 · #159) */
export * from "./warning-events"; /* Warning Events 화면(#208) — 타입만, 런타임 바이트 0 */
export * from "./ui-config"; /* UI 부트스트랩 설정 · 표시 언어(ADR 0025) */
export * from "./api-error"; /* 최상위 API 오류 봉투 · 안정 사유 코드(ADR 0025) */
export * from "./gitops"; /* Argo CD Application read-only projection(ADR 0072) */
